import logger from '@ayocore/platform/common/helpers/logger';
import type { ActivityCommentScope } from '@ayocore/activity/common/types/activity-comment-scope';
import type { ActivityComposerInsertFragment } from '../types/activity-composer-fragment';
import { ControllerStore } from './ControllerStore';
import { INITIAL_COMPOSER_STATE, type ActivityComposerState } from './types';

/**
 * ActivityComposerController — shared состояние composer (без high-frequency draft state).
 */
export class ActivityComposerController extends ControllerStore<ActivityComposerState> {
  private insertRequestSeq = 0;

  constructor() {
    super(INITIAL_COMPOSER_STATE);
  }

  /**
   * Полный сброс shared-state composer.
   */
  reset(): void {
    this.setState({
      selectedScope: null,
      isScopeBoundToDraft: false,
      pendingInsertFragment: null,
    });
    logger.debug('[Activity:Composer] reset');
  }

  /**
   * Обновляет выбранный scope (например, после #mention или preset из ChatController).
   */
  setSelectedScope(scope: ActivityCommentScope | null): void {
    this.setState({
      selectedScope: scope,
    });
    logger.debug('[Activity:Composer] setSelectedScope', {
      hasScope: Boolean(scope),
      entityType: scope?.entity_type ?? null,
    });
  }

  /**
   * Привязывает/отвязывает текущий scope к draft-состоянию composer.
   */
  setScopeBoundToDraft(isScopeBoundToDraft: boolean): void {
    this.setState({
      isScopeBoundToDraft,
    });
  }

  /**
   * Запрашивает фокус composer (one-shot сигнал для UI-слоя).
   */
  requestFocus(): void {
    const nextFocusRequestSeq = this.getSnapshot().focusRequestSeq + 1;
    this.setState({
      focusRequestSeq: nextFocusRequestSeq,
    });
  }

  /**
   * Запрашивает вставку фрагмента в composer (например, @mention из сообщения).
   */
  requestInsertFragment(fragment: ActivityComposerInsertFragment): void {
    this.insertRequestSeq += 1;
    this.setState({
      pendingInsertFragment: {
        requestId: this.insertRequestSeq,
        fragment,
      },
    });
  }

  /**
   * Подтверждает обработку pending вставки фрагмента (one-shot consume).
   */
  consumeInsertFragment(requestId: number): void {
    const pending = this.getSnapshot().pendingInsertFragment;
    if (!pending || pending.requestId !== requestId) return;
    this.setState({
      pendingInsertFragment: null,
    });
  }
}
